import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import ConfirmModal from "../../components/ConfirmModal";
import { EmptyState } from "../../components/EmptyState";
import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import { TextArea } from "../../components/Form/Input/textArea";
import Label from "../../components/Form/Label";
import { FormGroup } from "../../components/Form/styles";
import { ImageField } from "../../components/ImageField";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import ToggleSwitch from "../../components/ToggleSwitch";
import { CropProps } from "../../components/UploadImageInput";
import constants from "../../constants";
import { useAccountCurrency } from "../../hooks/AccountCurrency";
import PlusIcon from "../../icons/Plus";
import {
  AgendaService,
  AgendaServiceInput,
  createService,
  deleteService,
  listServicesWithMinimum,
  orderServices,
  updateService,
} from "../../services/agenda";
import { ListCardAction, VisibilityIcon } from "../Editor/components/ListCard";
import { RowActions } from "../Editor/components/RowActions";
import { ToggleRow as SettingsToggleRow } from "../Settings/styles";
import { NewSiteCard } from "../Sites/styles";
import { PAYMENT_CURRENCY } from "./BookingModal";
import { centsToField, currencySymbol, fieldToCents, formatCents } from "./format";
import { FieldError, ListStack, MenuStatus, Note } from "./styles";

const prefix = "pages.agenda.services";

const MAX_NAME = 80;

// The service's photo: square, as it shows on the agenda page
const PHOTO_CROP: CropProps = { slice: { aspect: 1 }, size: { width: 800, height: 800 }, cropShape: "rect" };

// The service's length: the usual ones (any other saved stays as an extra option)
const DURATIONS = [10, 15, 20, 30, 40, 45, 50, 60, 75, 90, 105, 120, 150, 180, 240, 300, 360, 480];

// The price as it's typed, in cents: "5" → "0,05", "5000" → "50,00" (empty: no price)
const maskPrice = (text: string) => {
  const digits = text
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "")
    .slice(0, 9);
  if (!digits) return "";
  const cents = digits.padStart(3, "0");
  const reais = cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${reais},${cents.slice(-2)}`;
};

type Draft = {
  name: string;
  description: string;
  duration: string;
  price: string;
  priceFrom: boolean;
  hidePrice: boolean;
  active: boolean;
  image: string | null;
};

const draftOf = (service?: AgendaService): Draft => ({
  name: service?.name ?? "",
  description: service?.description ?? "",
  duration: String(service?.durationMinutes ?? 30),
  price: centsToField(service?.priceCents),
  priceFrom: service?.priceFrom ?? false,
  hidePrice: service?.hidePrice ?? false,
  active: service?.active ?? true,
  image: service?.image ?? null,
});

type Props = { siteId: string };

/**
 * The agenda's services: what the customer books, with its time and price, in the order the page shows them. Set up
 * on any Keepo (PLUS is asked when publishing).
 */
export const Services = ({ siteId }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currency, loaded: currencyLoaded } = useAccountCurrency();
  const [services, setServices] = useState<AgendaService[]>();
  const [minSaleCents, setMinSaleCents] = useState<number>();
  // the list waits for the account's currency too: the browser's guess would show the prices in another one first
  const listed = currencyLoaded ? services : undefined;
  const [hasError, setError] = useState(false);
  const [editing, setEditing] = useState<AgendaService | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(draftOf());
  const [problem, setProblem] = useState<"name" | "duration" | "price" | "save" | null>(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<AgendaService | null>(null);
  // what failed to be done (not to be loaded)
  const [failed, setFailed] = useState<"remove" | "order" | null>(null);
  // each move asks for the whole order: only the last one asked decides what's on screen
  const lastOrder = useRef(0);

  useEffect(() => {
    let current = true;
    setServices(undefined);
    setError(false);
    listServicesWithMinimum(siteId)
      .then(({ items, minSaleCents: minimum }) => {
        if (!current) return;
        setServices(items);
        setMinSaleCents(minimum);
      })
      .catch(() => current && setError(true));
    return () => {
      current = false;
    };
  }, [siteId]);

  const open = (service: AgendaService | "new") => {
    setEditing(service);
    setDraft(draftOf(service === "new" ? undefined : service));
    setProblem(null);
  };

  const save = async () => {
    const duration = Number(draft.duration);
    const priceCents = fieldToCents(draft.price);
    if (!draft.name.trim()) return setProblem("name");
    if (!Number.isInteger(duration) || duration < 5 || duration > 720 || duration % 5 !== 0)
      return setProblem("duration");
    if (Number.isNaN(priceCents)) return setProblem("price");

    const input: AgendaServiceInput = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      durationMinutes: duration,
      priceCents,
      priceFrom: draft.priceFrom,
      hidePrice: draft.hidePrice,
      active: draft.active,
      image: draft.image,
    };
    setSaving(true);
    setProblem(null);
    try {
      if (editing === "new") {
        const created = await createService(siteId, input);
        setServices(shown => [...(shown ?? []), created]);
      } else if (editing) {
        const updated = await updateService(siteId, editing.id, input);
        setServices(shown => shown?.map(item => (item.id === updated.id ? updated : item)));
      }
      setEditing(null);
    } catch {
      setProblem("save");
    } finally {
      setSaving(false);
    }
  };

  // one place up (-1) or down (+1) in the order the Keepo's page shows them
  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (!services || to < 0 || to >= services.length) return;
    const next = [...services];
    [next[index], next[to]] = [next[to], next[index]];
    setServices(next);
    setFailed(null);
    const mine = ++lastOrder.current;
    orderServices(
      siteId,
      next.map(({ id }) => id)
    ).catch(() => {
      // an earlier move failing after a later one is fine: the later one has the whole order
      if (mine !== lastOrder.current) return;
      setFailed("order");
      listServicesWithMinimum(siteId)
        .then(({ items }) => mine === lastOrder.current && setServices(items))
        .catch(() => undefined);
    });
  };

  const remove = async () => {
    if (!removing) return;
    const gone = removing;
    setRemoving(null);
    setFailed(null);
    try {
      await deleteService(siteId, gone.id);
      setServices(shown => shown?.filter(item => item.id !== gone.id));
    } catch {
      setFailed("remove");
    }
  };

  const price = (service: AgendaService) => {
    const value = formatCents(service.priceCents, currency);
    if (!value) return t(`${prefix}.noPrice`);
    if (service.hidePrice) return t(`${prefix}.priceHidden`, { price: value });
    return service.priceFrom ? t(`${prefix}.priceFrom`, { price: value }) : value;
  };

  // the eye on the row: on or off the agenda at once (the list shows it before the API answers, and undoes a failure).
  // A service whose change is still on its way ignores the next click: two in a row would each undo the other's
  // failure.
  const [toggling, setToggling] = useState<string[]>([]);
  const toggleShown = async (service: AgendaService) => {
    if (toggling.includes(service.id)) return;
    const active = !service.active;
    const setActive = (value: boolean) =>
      setServices(shown => shown?.map(item => (item.id === service.id ? { ...item, active: value } : item)));
    setToggling(ids => [...ids, service.id]);
    setActive(active);
    try {
      await updateService(siteId, service.id, { active });
      toast.success(t(`${prefix}.${active ? "shownToast" : "hiddenToast"}`));
    } catch {
      setActive(service.active);
      toast.error(t(`${prefix}.toggleError`));
    } finally {
      setToggling(ids => ids.filter(id => id !== service.id));
    }
  };

  /** 30 min, 1h, 1h 30min */
  const durationText = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return t(`${prefix}.minutes`, { count: minutes });
    return rest ? t(`${prefix}.hoursMinutes`, { hours, minutes: rest }) : t(`${prefix}.hours`, { hours });
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft(current => ({ ...current, [key]: value }));

  return (
    <>
      <ListStack>
        {/* Before the list, as "Create Keepo" on /sites */}
        <NewSiteCard type="button" onClick={() => open("new")}>
          <span className="icon">
            <PlusIcon width={14} height={14} color="currentColor" />
          </span>
          {t(`${prefix}.new`)}
        </NewSiteCard>

        {hasError && <Note role="alert">{t(`${prefix}.loadError`)}</Note>}
        {failed && <Note role="alert">{t(`${prefix}.${failed}Error`)}</Note>}
        {!listed && !hasError && (
          <ServiceList aria-busy="true">
            {[0, 1].map(row => (
              <li key={row} className="skeleton">
                <span className="lines">
                  <Skeleton className="name" />
                  <Skeleton className="meta" />
                </span>
              </li>
            ))}
          </ServiceList>
        )}

        {/* no button: "+ Novo serviço" is right above */}
        {listed && listed.length === 0 && (
          <EmptyState emoji="🛎️" title={t(`${prefix}.emptyTitle`)} description={t(`${prefix}.emptyText`)} />
        )}
        {listed && listed.length > 0 && (
          // one frame per service (as /sites): the whole frame edits it, the rest is in its "⋯"
          <ServiceList aria-label={t("pages.agenda.tabs.services")}>
            {listed.map((service, index) => (
              <li key={service.id} aria-label={service.name} className={service.active ? undefined : "faded"}>
                <div
                  className="card clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => open(service)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      open(service);
                    }
                  }}>
                  {service.image && <img className="photo" src={service.image} alt="" />}
                  <span className="info">
                    <span className="row">
                      <h4>{service.name}</h4>
                      <MenuStatus $tone={service.active ? "on" : "off"}>
                        {t(`${prefix}.${service.active ? "shown" : "hidden"}`)}
                      </MenuStatus>
                    </span>
                    <p>
                      {price(service)} · {t(`${prefix}.minutes`, { count: service.durationMinutes })}
                    </p>
                    {constants.payments && service.onlineBlocker && minSaleCents !== undefined && (
                      <p className="blocker">
                        {/* online payments are always in reais, whatever the account's currency */}
                        {t(`${prefix}.onlineBlocker.${service.onlineBlocker}`, {
                          min: formatCents(minSaleCents, PAYMENT_CURRENCY),
                        })}
                      </p>
                    )}
                  </span>
                  <span
                    className="actions"
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}>
                    {/* on the agenda or not: the eye, as on the pages and the editor's sections */}
                    <ListCardAction
                      type="button"
                      aria-label={t(`${prefix}.${service.active ? "hide" : "show"}`, { name: service.name })}
                      title={t(`${prefix}.${service.active ? "hide" : "show"}`, { name: service.name })}
                      aria-busy={toggling.includes(service.id) || undefined}
                      onClick={() => toggleShown(service)}>
                      <VisibilityIcon visible={service.active} />
                    </ListCardAction>
                    <RowActions
                      label={t(`${prefix}.more`, { name: service.name })}
                      actions={[
                        { label: t(`${prefix}.edit`), onSelect: () => open(service) },
                        { label: t(`${prefix}.moveUp`), disabled: index === 0, onSelect: () => move(index, -1) },
                        {
                          label: t(`${prefix}.moveDown`),
                          disabled: index === listed.length - 1,
                          onSelect: () => move(index, 1),
                        },
                        { label: t(`${prefix}.remove`), danger: true, onSelect: () => setRemoving(service) },
                      ]}
                    />
                  </span>
                </div>
              </li>
            ))}
          </ServiceList>
        )}
      </ListStack>

      {editing && (
        <Modal
          title={t(editing === "new" ? `${prefix}.newTitle` : `${prefix}.editTitle`)}
          escapeExit
          busy={saving}
          onExit={() => setEditing(null)}
          error={problem === "save" ? t(`${prefix}.saveError`) : undefined}
          footer={
            <>
              <ModalSecondaryButton type="button" onClick={() => setEditing(null)}>
                {t(`${prefix}.cancel`)}
              </ModalSecondaryButton>
              <ModalButton loading={saving} onClick={save}>
                {t(`${prefix}.save`)}
              </ModalButton>
            </>
          }>
          <Photo>
            <ImageField
              inset
              shape="square"
              crop={PHOTO_CROP}
              accept="image/png,image/jpeg,image/webp"
              title={t(`${prefix}.photo`)}
              hint={t(`${prefix}.photoHint`)}
              image={draft.image ?? undefined}
              onChange={image => set("image", image ?? null)}
            />
          </Photo>
          {/* each group stacks in reverse (label on top): an error comes first, to show under its field */}
          <FormGroup>
            {problem === "name" && <FieldError>{t(`${prefix}.nameRequired`)}</FieldError>}
            <Input
              id="service-name"
              type="text"
              label={t(`${prefix}.name`)}
              value={draft.name}
              maxLength={MAX_NAME}
              required
              onChange={event => set("name", event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <TextArea
              id="service-description"
              rows={2}
              maxLength={1000}
              value={draft.description}
              onChange={event => set("description", event.target.value)}
            />
            <Label htmlFor="service-description" text={t(`${prefix}.description`)} ignoreLengthInfo />
          </FormGroup>
          <Pair>
            <FormGroup>
              {problem === "duration" && <FieldError>{t(`${prefix}.durationInvalid`)}</FieldError>}
              <Select
                id="service-duration"
                value={draft.duration}
                onChange={event => set("duration", event.target.value)}>
                {(DURATIONS.includes(Number(draft.duration))
                  ? DURATIONS
                  : [...DURATIONS, Number(draft.duration)].sort((a, b) => a - b)
                ).map(minutes => (
                  <option key={minutes} value={String(minutes)}>
                    {durationText(minutes)}
                  </option>
                ))}
              </Select>
              <Label htmlFor="service-duration" text={t(`${prefix}.duration`)} ignoreLengthInfo />
            </FormGroup>
            <FormGroup>
              {problem === "price" && <FieldError>{t(`${prefix}.priceInvalid`)}</FieldError>}
              <Input
                id="service-price"
                type="text"
                label={t(`${prefix}.price`)}
                prefix={currencySymbol(currency)}
                placeholder="0,00"
                value={draft.price}
                ignoreLengthInfo
                onChange={event => set("price", maskPrice(event.target.value))}
              />
            </FormGroup>
          </Pair>
          <Options>
            <SettingsToggleRow>
              <div className="text">
                <h6>{t(`${prefix}.priceFromToggle`)}</h6>
                <p className="description">{t(`${prefix}.priceFromHint`)}</p>
              </div>
              <ToggleSwitch
                value={draft.priceFrom}
                // a price "a partir de" shows: it isn't hidden
                setValue={value =>
                  setDraft(current => ({ ...current, priceFrom: value, hidePrice: value ? false : current.hidePrice }))
                }
                label={t(`${prefix}.priceFromToggle`)}
              />
            </SettingsToggleRow>
            <div className="divider" />
            <SettingsToggleRow>
              <div className="text">
                <h6>{t(`${prefix}.hidePriceToggle`)}</h6>
                <p className="description">{t(`${prefix}.hidePriceHint`)}</p>
              </div>
              <ToggleSwitch
                value={draft.hidePrice}
                // nothing shows a hidden price: it isn't "a partir de" either
                setValue={value =>
                  setDraft(current => ({ ...current, hidePrice: value, priceFrom: value ? false : current.priceFrom }))
                }
                label={t(`${prefix}.hidePriceToggle`)}
              />
            </SettingsToggleRow>
          </Options>
        </Modal>
      )}

      {removing && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.removeTitle`, { name: removing.name })}
          descriptions={[t(`${prefix}.removeText`)]}
          confirmButtonTitle={t(`${prefix}.remove`)}
          cancelTitle={t(`${prefix}.cancel`)}
          onConfirm={remove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  );
};

/** As the day's bookings: one frame each, a dark tag on the left, the name and its status, the price under it. */
const ServiceList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
  list-style: none;
  font-family: Inter, sans-serif;

  .card {
    display: flex;
    gap: 1rem;
    align-items: center;
    min-width: 0;
    padding: 0.75rem 1rem;
    background: var(--color-neutral-dark);
    border: 1px solid transparent;
    border-radius: 1rem;
    transition: border-color 0.15s;
  }

  .card.clickable {
    cursor: pointer;
  }

  .photo {
    flex: none;
    width: 2.75rem;
    height: 2.75rem;
    object-fit: cover;
    border-radius: 0.625rem;
  }

  .card.clickable:hover {
    border-color: var(--color-neutral-base);
  }

  .faded h4 {
    color: var(--color-neutral-150);
  }

  .info {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  .row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
  }

  .row > span {
    flex: none;
    margin-top: 0;
  }

  h4 {
    overflow: hidden;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }

  /* why it can't be charged online: a quiet warning, in a request's orange */
  p.blocker {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: #ffb066;
  }

  /* the eye and the "⋯" on the row's right */
  .actions {
    display: flex;
    flex: none;
    gap: 0.5rem;
    align-items: center;
  }

  .skeleton {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 0.75rem 1rem;
    background: var(--color-neutral-dark);
    border-radius: 1rem;
  }

  .skeleton .lines {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.5rem;
  }

  .skeleton .name {
    width: 40%;
    height: 0.875rem;
  }

  .skeleton .meta {
    width: 25%;
    height: 0.75rem;
  }
`;

/** Duration and price side by side. */
const Pair = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
  /* by their boxes' bottom: the two fields' labels aren't the same height */
  align-items: end;
  margin-bottom: 1rem;

  ${FormGroup} {
    margin-bottom: 0;
  }

  /* the field pulls its text close to its prefix: a little room after the "R$" */
  #service-price {
    margin-left: -0.375rem;
  }
`;

/** The service's two switches, in a frame, each with what it does under its name. */
const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-darkness);
  border-radius: 0.75rem;

  h6 {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .divider {
    height: 1px;
    background: var(--color-neutral-base);
  }
`;

/** The photo first, in the modal's width (as the share image on SEO). */
const Photo = styled.div`
  margin-bottom: 1rem;
`;
